import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Paper,
  Tabs,
  Tab,
  SelectChangeEvent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';
import LanguageIcon from '@mui/icons-material/Language';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import StorageIcon from '@mui/icons-material/Storage';
import UploadFileIcon from '@mui/icons-material/UploadFile';

// Import services
import { getQna, addQna, getAis, updateQna, deleteQna, processFiles, deleteAllQna, deleteFilteredQna, importQnaFromCSV } from '../services/api';

// Import types
import { ExtendedQnaPair, Collection, AI } from '../types';

// Import CSV utilities
import { parseCSVToQnaPairs, generateCSVPreview } from '../utils/csvUtils';

// Import components
import TabPanel from '../components/TabPanel';
import ManualInputTab from '../components/tabs/ManualInputTab';
import WebpageInputTab from '../components/tabs/WebpageInputTab';
import FileInputTab from '../components/tabs/FileInputTab';
import DatabaseTab from '../components/tabs/DatabaseTab';
import SearchPanel from '../components/SearchPanel';
import QnaList from '../components/QnaList';
import CollectionInfo from '../components/CollectionInfo';


const QnaData = () => {
  const { aiId } = useParams<{ aiId: string }>();
  const [searchParams] = useSearchParams();
  
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  
  
  // State for QnA data
  const [qnaList, setQnaList] = useState<ExtendedQnaPair[]>([]);
  const [filteredQnaList, setFilteredQnaList] = useState<ExtendedQnaPair[]>([]);
  
  // UI state
  const [initialLoading, setInitialLoading] = useState(false);
  const [addingQna, setAddingQna] = useState(false);
  const [deletingQnaIds, setDeletingQnaIds] = useState<string[]>([]);
  const [editingQnaIds, setEditingQnaIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [aiDetails, setAiDetails] = useState<AI | null>(null);
  const [collection, setCollection] = useState<Collection | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Delete All confirmation dialog state
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  
  // Process status and loading states
  const [processingFiles, setProcessingFiles] = useState(false);
  const [processStatus, setProcessStatus] = useState<{ success: boolean; message: string } | null>(null);
  
  // CSV import states
  const [importingCSV, setImportingCSV] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<any>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  
  // Search state
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [searching, setSearching] = useState(false);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // File upload ref
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  
  
  // Current collection ID
  const [currentCollectionId, setCurrentCollectionId] = useState<string | null>(null);
  

  // Helper function to update Q&A list while preserving order
  const updateQnaListPreservingOrder = (updateFunction: (list: ExtendedQnaPair[]) => ExtendedQnaPair[]) => {
    setQnaList(updateFunction);
    setFilteredQnaList(updateFunction);
  };


  // Load AI details and set current collection when component mounts
  useEffect(() => {
    if (aiId) {
      setInitialLoading(true);
      setError(null);
      
      // Get AI details
      getAis()
        .then((res) => {
          const currentAi = res.data.find((ai: AI) => ai.id === aiId);
          setAiDetails(currentAi || null);
          
          // Set the current collection (use first available collection or from URL params)
          if (currentAi && currentAi.collections && currentAi.collections.length > 0) {
            const urlCollectionId = searchParams.get('collectionId');
            const targetCollection = urlCollectionId 
              ? currentAi.collections.find((c: Collection) => c._id === urlCollectionId)
              : currentAi.collections[0];
            
            if (targetCollection) {
              setCurrentCollectionId(targetCollection._id);
              setCollection(targetCollection);
            }
          }
          
          setInitialLoading(false);
        })
        .catch((err) => {
          console.error('Error loading AI details:', err);
          
          // If we get a 401 Unauthorized error, redirect to login
          if (err.response && err.response.status === 401) {
            setError(null);
            navigate('/login', { replace: true });
          } else {
            setError('Error loading AI details: ' + (err.response?.data?.error || err.message));
          }
          
          setInitialLoading(false);
        });
    }
  }, [aiId, navigate, searchParams]);

  // Load Q&A data when collection ID is available
  useEffect(() => {
    if (aiId && currentCollectionId) {
      // Fetch the Q&A data for this collection
      getQna(aiId, currentCollectionId)
        .then((res) => {
          // Sort the data so newest items appear first (reverse the array)
          const sortedData = [...res.data].reverse();
          setQnaList(sortedData);
          setFilteredQnaList(sortedData);
          
          // Extract all unique tags from the Q&A pairs
          const allTags = new Set<string>();
          sortedData.forEach((qna: ExtendedQnaPair) => {
            if (qna.tags && Array.isArray(qna.tags)) {
              qna.tags.forEach(tag => allTags.add(tag));
            }
          });
          setAvailableTags(Array.from(allTags));
        })
        .catch((err) => {
          console.error('Error loading Q&A data:', err);
          setError('Error loading Q&A data: ' + (err.response?.data?.error || err.message));
        });
    }
  }, [aiId, currentCollectionId]);

  // Function to perform search based on current search criteria
  const handleSearch = (
    textQuery: string,
    tagQuery: string,
    sourceFilter: string,
    sortByOrder: 'newest' | 'oldest'
  ) => {
    
    // Cancel any active edits before searching
    cancelAllActiveEdits();
    
    setSearching(true);
    let filtered = [...qnaList];
    // Text search
    if (textQuery) {
      const lowercaseQuery = textQuery.toLowerCase();
      filtered = filtered.filter(qna =>
        qna.question.toLowerCase().includes(lowercaseQuery) ||
        qna.answer.toLowerCase().includes(lowercaseQuery)
      );
    }
    // Tag search
    if (tagQuery) {
      filtered = filtered.filter(qna =>
        qna.tags && Array.isArray(qna.tags) && qna.tags.includes(tagQuery)
      );
    }
    // Source filter
    if (sourceFilter && sourceFilter !== 'all') {
      filtered = filtered.filter(qna => {
        if (!qna.tags || !Array.isArray(qna.tags)) return false;
        switch (sourceFilter) {
          case 'manual':
            return qna.tags.includes('Manual');
          case 'webpage':
            return qna.tags.some(tag => tag.startsWith('http'));
          case 'database':
            return qna.tags.some(tag => tag.startsWith('database-'));
          case 'file':
            return qna.tags.some(tag =>
              !tag.includes('Manual') &&
              !tag.startsWith('http') &&
              !tag.startsWith('database-')
            );
          default:
            return true;
        }
      });
    }
    // Ordering
    filtered = applySortOrder(filtered, sortByOrder);
    setSortOrder(sortByOrder);
    setFilteredQnaList(filtered);
    setPage(0);
    setSearching(false);
  };
  
  
  // Apply sort order to the filtered list
  const applySortOrder = (list: ExtendedQnaPair[], order: string) => {
    switch (order) {
      case 'newest':
        // Assuming IDs are sequential or contain timestamps
        return [...list].sort((a, b) => b.qnaId.localeCompare(a.qnaId));
      case 'oldest':
        return [...list].sort((a, b) => a.qnaId.localeCompare(b.qnaId));
      case 'relevance':
      default:
        return list; // Keep current order
    }
  };
  
  // Reset all search filters
  const handleClearSearch = () => {
    
    setFilteredQnaList(qnaList);
  };
  
  // Handlers for tabs
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle adding new Q&A manually
  const handleAdd = (questionText: string, answerText: string) => {
    const collectionId = currentCollectionId;
    if (!aiId || !collectionId || !questionText || !answerText) {
      setError('Please provide both question and answer');
      return;
    }
    
    
    // Create a temporary ID for optimistic UI update
    const tempId = `temp-${Date.now()}`;
    
    // Add "Manual" tag for manually created Q&A pairs
    const tags = ["Manual"];
    
    // Optimistically add the new Q&A pair to the UI
    const newQnaPair: ExtendedQnaPair = {
      qnaId: tempId,
      question: questionText,
      answer: answerText,
      tags: tags
    };
    
    // Update both the full list and filtered list immediately
    // Add new items at the beginning of the lists to show newest first
    const updatedQnaList = [newQnaPair, ...qnaList];
    setQnaList(updatedQnaList);
    setFilteredQnaList([newQnaPair, ...filteredQnaList]);
    
    // Mark as adding but don't block the entire UI
    setAddingQna(true);
    
    // Send the request to the server with tags
    addQna(aiId, collectionId, { 
      question: newQnaPair.question, 
      answer: newQnaPair.answer,
      tags: tags 
    })
      .then((res) => {
        // Replace the temporary item with the real one from the server using updater functions
        setQnaList(prevList => 
          prevList.map(qna => 
            qna.qnaId === tempId ? res.data : qna
          )
        );
        
        setFilteredQnaList(prevFilteredList => 
          prevFilteredList.map(qna => 
            qna.qnaId === tempId ? res.data : qna
          )
        );
        
        setAddingQna(false);
      })
      .catch((err) => {
        console.error('Error adding QnA:', err);
        setError('Failed to add Q&A pair: ' + (err.response?.data?.error || err.message));
        
        // Remove the temporary item on failure from both lists using updater functions
        setQnaList(prevList => prevList.filter(qna => qna.qnaId !== tempId));
        setFilteredQnaList(prevFilteredList => prevFilteredList.filter(qna => qna.qnaId !== tempId));
        setAddingQna(false);
      });
  };

  // Handle deletion of Q&A pair
  const handleDelete = (qnaId: string) => {
    const collectionId = currentCollectionId;
    if (!aiId || !collectionId) return;
    
    // Add this ID to the deleting state
    setDeletingQnaIds(prev => [...prev, qnaId]);
    
    // Optimistically update UI
    setQnaList(prev => 
      prev.map(qna => 
        qna.qnaId === qnaId 
          ? { ...qna, isDeleting: true } 
          : qna
      )
    );
    
    // Send delete request
    deleteQna(aiId, collectionId, qnaId)
      .then(() => {
        // Remove from the list on success
        const updatedList = qnaList.filter(qna => qna.qnaId !== qnaId);
        setQnaList(updatedList);
        setFilteredQnaList(updatedList);
        setDeletingQnaIds(prev => prev.filter(id => id !== qnaId));
      })
      .catch((err) => {
        console.error('Error deleting QnA:', err);
        setError('Failed to delete Q&A pair: ' + (err.response?.data?.error || err.message));
        
        // Revert the optimistic update on failure
        setQnaList(prev => 
          prev.map(qna => 
            qna.qnaId === qnaId 
              ? { ...qna, isDeleting: false } 
              : qna
          )
        );
        setDeletingQnaIds(prev => prev.filter(id => id !== qnaId));
      });
  };

  // Handle editing Q&A pair
  const handleEdit = (qnaId: string) => {
    // Find the QnA pair to edit
    const qnaPair = qnaList.find(qna => qna.qnaId === qnaId);
    if (!qnaPair) return;
    
    // Cancel any existing edits before starting a new one
    cancelAllActiveEdits();
    
    // Add this ID to the editing state
    setEditingQnaIds(prev => [...prev, qnaId]);
    
    // Set it to editing mode with current values pre-filled
    setQnaList(prev => 
      prev.map(qna => 
        qna.qnaId === qnaId 
          ? { 
              ...qna, 
              isEditing: true,
              editedQuestion: qna.question,
              editedAnswer: qna.answer 
            } 
          : qna
      )
    );
  };

  // Helper function to cancel all active edits
  const cancelAllActiveEdits = () => {
    if (editingQnaIds.length === 0) return; // No active edits to cancel
    
    // Clear all editing IDs
    setEditingQnaIds([]);
    
    // Remove editing state from all items in qnaList
    setQnaList(prev => 
      prev.map(qna => 
        editingQnaIds.includes(qna.qnaId)
          ? { 
              ...qna, 
              isEditing: false,
              editedQuestion: undefined,
              editedAnswer: undefined 
            } 
          : qna
      )
    );
  };

  // Handle canceling edit mode
  const handleCancelEdit = (qnaId: string) => {
    // Remove from editing state and revert changes
    setEditingQnaIds(prev => prev.filter(id => id !== qnaId));
    setQnaList(prev => 
      prev.map(qna => 
        qna.qnaId === qnaId 
          ? { 
              ...qna, 
              isEditing: false,
              editedQuestion: undefined,
              editedAnswer: undefined 
            } 
          : qna
      )
    );
  };

  // Handle saving edited Q&A pair
  const handleSaveEdit = (qnaId: string) => {
    const collectionId = currentCollectionId;
    if (!aiId || !collectionId) return;
    
    const qnaPair = qnaList.find(qna => qna.qnaId === qnaId);
    if (!qnaPair || !qnaPair.editedQuestion || !qnaPair.editedAnswer) return;
    
    // Get the updated values
    const updatedQuestion = qnaPair.editedQuestion.trim();
    const updatedAnswer = qnaPair.editedAnswer.trim();
    
    // Before making the API call, store the current editing state
    const editingItem = { ...qnaPair };
    
    // Remove from editing state
    setEditingQnaIds(prev => prev.filter(id => id !== qnaId));
    
    // Update server
    updateQna(aiId, collectionId, qnaId, { 
      question: updatedQuestion, 
      answer: updatedAnswer,
      tags: qnaPair.tags
    })
      .then((res) => {
        
        // Create a fully updated item with all the server data
        // Preserve the original dates in case the server response doesn't include them
        const updatedItem = {
          ...qnaPair, // Start with original item to preserve dates
          ...res.data, // Override with server response data
          isEditing: false,
          editedQuestion: undefined,
          editedAnswer: undefined,
        };
        
        // Update main list 
        setQnaList(prev => 
          prev.map(qna => qna.qnaId === qnaId ? updatedItem : qna)
        );
        
        // Update filtered list only if the item is currently in it
        // This preserves the current filter state
        setFilteredQnaList(prev => {
          const itemInFilteredList = prev.some(qna => qna.qnaId === qnaId);
          if (itemInFilteredList) {
            return prev.map(qna => qna.qnaId === qnaId ? updatedItem : qna);
          }
          return prev; // Don't modify if item isn't in filtered list
        });
        
        // Show success message
        setProcessStatus({
          success: true,
          message: 'Q&A pair updated successfully'
        });
        
        // Clear the message after 3 seconds
        setTimeout(() => {
          setProcessStatus(null);
        }, 3000);
      })
      .catch((err) => {
        console.error('Error updating QnA:', err);
        setError('Failed to update Q&A pair: ' + (err.response?.data?.error || err.message));
        
        // If error, revert to original state and re-enable editing
        setQnaList(prev => 
          prev.map(qna => 
            qna.qnaId === qnaId 
              ? { 
                  ...editingItem, 
                  isEditing: true,
                } 
              : qna
          )
        );
        
        // Only revert in filtered list if item was there
        setFilteredQnaList(prev => {
          const itemInFilteredList = prev.some(qna => qna.qnaId === qnaId);
          if (itemInFilteredList) {
            return prev.map(qna => 
              qna.qnaId === qnaId 
                ? { 
                    ...editingItem, 
                    isEditing: true,
                  } 
                : qna
            );
          }
          return prev;
        });
        
        setEditingQnaIds(prev => [...prev, qnaId]);
      });
  };

  // Handle updating edited fields
  const handleUpdateEditField = (qnaId: string, field: 'question' | 'answer', value: string) => {
    // Use preserving order function and maintain highlighting state
    updateQnaListPreservingOrder(prev => 
      prev.map(qna => 
        qna.qnaId === qnaId 
          ? { 
              ...qna, 
              [field === 'question' ? 'editedQuestion' : 'editedAnswer']: value,
            } 
          : qna
      )
    );
  };


  // Handle processing files
  const handleProcessFiles = (files: File[], qaPairsCount: number) => {
    const collectionId = currentCollectionId;
    if (!aiId || !collectionId || !files.length) return;
    
    
    setProcessingFiles(true);
    setProcessStatus(null);
    setError(null);
    
    // Use the processFiles API function which will convert files to base64
    processFiles(aiId, collectionId, files, qaPairsCount)
      .then((res) => {
        console.log('Process files response:', res.data);
        
        // Instead of making a new request, update the list with the returned pairs if available
        if (res.data && Array.isArray(res.data.addedPairs) && res.data.addedPairs.length > 0) {
          // Add the new pairs to the beginning of the existing lists to show newest first
          const updatedList = [...res.data.addedPairs, ...qnaList];
          setQnaList(updatedList);
          setFilteredQnaList([...res.data.addedPairs, ...filteredQnaList]);
          
          setProcessStatus({ 
            success: true, 
            message: `Successfully processed ${files.length} files and added ${res.data.addedPairs.length} Q&A pairs` 
          });
        } else {
          // If we don't have pairs in the response, fetch the updated list
          return getQna(aiId, collectionId).then(response => {
            // Sort the data so newest items appear first
            const sortedData = [...response.data].reverse();
            setQnaList(sortedData);
            setFilteredQnaList(sortedData);
            setProcessStatus({ 
              success: true, 
              message: `Successfully processed ${files.length} files and generated Q&A pairs` 
            });
          });
        }
        // Also clear the file input to allow uploading the same file again
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      })
      .catch((err) => {
        console.error('Error processing files:' , err);
        setError('Failed to process files: ' + (err.response?.data?.error || err.message));
        setProcessStatus({ 
          success: false, 
          message: 'Failed to process files' 
        });
      })
      .finally(() => {
        setProcessingFiles(false);
      });
  };

  // Handle CSV file selection
  const handleCsvFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setCsvFile(file);
    setCsvPreview(null);

    // Read and parse the CSV file
    const reader = new FileReader();
    reader.onload = (e) => {
      const csvContent = e.target?.result as string;
      const parseResult = parseCSVToQnaPairs(csvContent, file.name);
      const preview = generateCSVPreview(parseResult, 5);
      
      setCsvPreview(preview);
      
      if (parseResult.errors.length > 0) {
        setError(`CSV parsing warnings: ${parseResult.errors.slice(0, 3).join(', ')}`);
      } else {
        setError(null);
      }
      
      setShowImportDialog(true);
    };
    
    reader.onerror = () => {
      setError('Failed to read CSV file');
    };
    
    reader.readAsText(file);
  };

  // Handle CSV import confirmation
  const handleConfirmImport = async () => {
    if (!csvPreview || !csvFile || !aiId || !currentCollectionId) return;
    
    setImportingCSV(true);
    setShowImportDialog(false);
    setError(null);
    
    try {
      console.log('Starting CSV import...');
      
      // Parse the CSV content again to get the data
      const reader = new FileReader();
      reader.onload = async (e) => {
        const csvContent = e.target?.result as string;
        const parseResult = parseCSVToQnaPairs(csvContent, csvFile.name);
        
        if (parseResult.success && parseResult.data.length > 0) {
          const result = await importQnaFromCSV(aiId, currentCollectionId, parseResult.data, csvFile.name, 10);
          
          console.log('Import result:', result.data);
          
          setProcessStatus({
            success: true,
            message: `Successfully imported ${result.data.successCount} Q&A pairs`
          });
          
          // Refresh the Q&A list
          const qnaResult = await getQna(aiId, currentCollectionId);
          const sortedData = [...qnaResult.data].reverse();
          setQnaList(sortedData);
          setFilteredQnaList(sortedData);
          
          // Update available tags
          const allTags = new Set<string>();
          sortedData.forEach((qna: ExtendedQnaPair) => {
            if (qna.tags && Array.isArray(qna.tags)) {
              qna.tags.forEach(tag => allTags.add(tag));
            }
          });
          setAvailableTags(Array.from(allTags));
          
        } else {
          setError('No valid Q&A pairs found in CSV file');
        }
      };
      
      reader.readAsText(csvFile);
      
    } catch (error) {
      console.error('Import error:', error);
      setError('Failed to import CSV: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setImportingCSV(false);
      setCsvFile(null);
      setCsvPreview(null);
    }
  };

  // Handle cancel import
  const handleCancelImport = () => {
    setShowImportDialog(false);
    setCsvFile(null);
    setCsvPreview(null);
  };

  // Handle page change for pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(parseInt(event.target.value.toString(), 10));
    setPage(0); // Reset to first page
  };

  // Handle deleting all Q&A pairs (or filtered pairs if filters are active)
  const handleDeleteAll = (filteredPairs?: ExtendedQnaPair[]) => {
    const collectionId = currentCollectionId;
    if (!aiId || !collectionId) return;

    setDeletingAll(true);
    setError(null);
    setProcessStatus(null);

    // Determine if we're deleting filtered pairs or all pairs
    const isFiltered = filteredPairs && filteredPairs.length !== qnaList.length;
    const deletePromise = isFiltered 
      ? deleteFilteredQna(aiId, collectionId, filteredPairs!.map(qna => qna.qnaId))
      : deleteAllQna(aiId, collectionId);

    deletePromise
      .then(() => {
        if (isFiltered) {
          // Remove only the filtered pairs from both lists
          const filteredIds = filteredPairs!.map(qna => qna.qnaId);
          const updatedQnaList = qnaList.filter(qna => !filteredIds.includes(qna.qnaId));
          setQnaList(updatedQnaList);
          setFilteredQnaList(updatedQnaList);
          setProcessStatus({
            success: true,
            message: `Successfully deleted ${filteredPairs!.length} filtered Q&A pairs`,
          });
        } else {
          // Delete all pairs
          setQnaList([]);
          setFilteredQnaList([]);
          setProcessStatus({
            success: true,
            message: 'Successfully deleted all Q&A pairs',
          });
        }
        setDeleteAllDialogOpen(false);
      })
      .catch((err) => {
        console.error('Error deleting QnA:', err);
        const message = isFiltered ? 'Failed to delete filtered Q&A pairs' : 'Failed to delete all Q&A pairs';
        setError(message + ': ' + (err.response?.data?.error || err.message));
      })
      .finally(() => {
        setDeletingAll(false);
      });
  };

  // Handle clicking on a tag for filtering
  const handleTagClick = (tag: string) => {
    // Cancel any active edits before filtering by tag
    cancelAllActiveEdits();
    
    // Set the tag and trigger a search
    handleSearch('', '', tag, sortOrder);
  };

  // Handle manual sort order change from QnaList
  const handleSortOrderChange = (newOrder: 'newest' | 'oldest') => {
    setSortOrder(newOrder);
  };

  // When user changes sortOrder, only update filtered list for newest/oldest
  useEffect(() => {
    // Don't re-filter if there are active edits to prevent list reloading
    if (editingQnaIds.length > 0) {
      return;
    }
    
    // Only apply ordering when we have actual data and a valid sort order
    if (qnaList.length > 0 && (sortOrder === 'newest' || sortOrder === 'oldest')) {
      setFilteredQnaList(applySortOrder(qnaList, sortOrder));
    }
  }, [sortOrder, editingQnaIds, qnaList]);




  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
        Q&A Data {aiDetails ? `for ${aiDetails.title}` : ''}
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {processStatus && (
        <Alert 
          severity={processStatus.success ? "success" : "error"} 
          sx={{ mb: 2 }} 
          onClose={() => setProcessStatus(null)}
        >
          {processStatus.message}
        </Alert>
      )}
      
      {collection && (
        <>
          <CollectionInfo
            collection={collection}
          />
          
          <Paper sx={{ mb: 4 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
              aria-label="Q&A input methods"
              scrollButtons={isMobile ? "auto" : false}
              allowScrollButtonsMobile
            >
              <Tab icon={<CreateIcon />} label="Manual" id="qna-tab-0" aria-controls="qna-tabpanel-0" />
              <Tab icon={<LanguageIcon />} label="Webpage" id="qna-tab-1" aria-controls="qna-tabpanel-1" />
              <Tab icon={<InsertDriveFileIcon />} label="File" id="qna-tab-2" aria-controls="qna-tabpanel-2" />
              <Tab icon={<StorageIcon />} label="Database" id="qna-tab-3" aria-controls="qna-tabpanel-3" />
              <Tab icon={<UploadFileIcon />} label="Import CSV" id="qna-tab-4" aria-controls="qna-tabpanel-4" />
            </Tabs>

            {/* Manual input tab */}
            <TabPanel value={tabValue} index={0}>
              <ManualInputTab 
                isAdding={addingQna}
                onAdd={handleAdd}
              />
            </TabPanel>            {/* Webpage input tab */}
            <TabPanel value={tabValue} index={1}>
              <WebpageInputTab 
                aiId={aiId!}
                collectionId={currentCollectionId!}
                onQAGenerated={(addedPairs) => {
                  // Add the new Q&A pairs to the beginning of the existing lists
                  const updatedList = [...addedPairs, ...qnaList];
                  setQnaList(updatedList);
                  setFilteredQnaList([...addedPairs, ...filteredQnaList]);
                  
                  // Update available tags
                  const allTags = new Set<string>();
                  updatedList.forEach((qna: any) => {
                    if (qna.tags && Array.isArray(qna.tags)) {
                      qna.tags.forEach((tag: string) => allTags.add(tag));
                    }
                  });
                  setAvailableTags(Array.from(allTags));
                }}
              />
            </TabPanel>

            {/* File input tab */}
            <TabPanel value={tabValue} index={2}>
              <FileInputTab 
                isProcessing={processingFiles}
                onProcessFiles={handleProcessFiles}
              />
            </TabPanel>

            {/* Database input tab */}
            <TabPanel value={tabValue} index={3}>
              <DatabaseTab 
                aiId={aiId as string}
                collectionId={currentCollectionId as string}
                onRefreshQnaPairs={() => {
                  // Fetch the latest Q&A pairs when new ones are generated
                  const collectionId = currentCollectionId;
                  if (aiId && collectionId) {
                    getQna(aiId, collectionId)
                      .then((res) => {
                        // Sort the data so newest items appear first
                        const sortedData = [...res.data].reverse();
                        setQnaList(sortedData);
                        setFilteredQnaList(sortedData);
                      })
                      .catch((err) => {
                        console.error('Error refreshing QnA list:', err);
                      });
                  }
                }}
              />
            </TabPanel>

            {/* CSV Import tab */}
            <TabPanel value={tabValue} index={4}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Import Q&A Pairs from CSV
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload a CSV file with Q&A pairs. Works with Google Sheets exports and manual CSV files. 
                  The first two columns should contain questions and answers. Headers are optional.
                  Imported pairs will be tagged with the filename and can be updated by uploading the same file again.
                  Large imports may take a few minutes to process and appear in the list.
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCsvFileSelect}
                    style={{ display: 'none' }}
                    id="csv-file-input"
                  />
                  <label htmlFor="csv-file-input">
                    <Button 
                      variant="contained" 
                      component="span" 
                      disabled={importingCSV}
                      startIcon={<UploadFileIcon />}
                    >
                      Choose CSV File
                    </Button>
                  </label>
                </Box>
                
                {importingCSV && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={20} />
                    <Typography>Importing Q&A pairs...</Typography>
                  </Box>
                )}
              </Box>
            </TabPanel>
          </Paper>
          
          {/* Search Panel */}
          <Paper sx={{ mb: 4 }}>
            <SearchPanel
              availableTags={availableTags}
              searching={searching}
              onSearch={handleSearch}
              onClearSearch={handleClearSearch}
            />
          </Paper>
          
          {/* QnA List */}
          <QnaList 
            qnaList={qnaList}
            filteredQnaList={filteredQnaList}
            deletingQnaIds={deletingQnaIds}
            editingQnaIds={editingQnaIds}
            page={page}
            rowsPerPage={rowsPerPage}
            deletingAll={deletingAll}
            deleteAllDialogOpen={deleteAllDialogOpen}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onSave={handleSaveEdit}
            onCancel={handleCancelEdit}
            onUpdateField={handleUpdateEditField}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            onDeleteAll={handleDeleteAll}
            onTagClick={handleTagClick}
            onOpenDeleteAllDialog={() => setDeleteAllDialogOpen(true)}
            onCloseDeleteAllDialog={() => setDeleteAllDialogOpen(false)}
            onSemanticSearch={() => {}}
            sortOrder={sortOrder}
            onSortOrderChange={handleSortOrderChange}
          />
          
          {/* CSV Import Preview Dialog */}
          <Dialog
            open={showImportDialog}
            onClose={handleCancelImport}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
          >
            <DialogTitle>
              Import Preview
            </DialogTitle>
            <DialogContent>
              {csvPreview && (
                <>
                  <DialogContentText sx={{ mb: 2 }}>
                    Ready to import {csvPreview.summary.validRows} valid Q&A pairs 
                    {csvPreview.summary.errorCount > 0 && ` (${csvPreview.summary.errorCount} errors)`}
                    {csvFile && (
                      <>
                        <br />
                        <strong>File:</strong> {csvFile.name}
                        <br />
                        <strong>Tag:</strong> {csvFile.name.replace(/\.[^/.]+$/, '')} (Source: Import)
                      </>
                    )}
                  </DialogContentText>
                  
                  {csvPreview.summary.validRows > 0 && (
                    <>
                      <Typography variant="h6" sx={{ mb: 1 }}>Preview (showing first 5):</Typography>
                      <TableContainer component={Paper} sx={{ mb: 2, maxHeight: 300 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Question</TableCell>
                              <TableCell>Answer</TableCell>
                              <TableCell>Tags</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {csvPreview.sampleData.map((item: any, index: number) => (
                              <TableRow key={index}>
                                <TableCell>{item.question?.substring(0, 100)}{item.question?.length > 100 ? '...' : ''}</TableCell>
                                <TableCell>{item.answer?.substring(0, 100)}{item.answer?.length > 100 ? '...' : ''}</TableCell>
                                <TableCell>{item.tags?.join(', ') || 'None'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                  
                  {csvPreview.errors.length > 0 && (
                    <>
                      <Typography variant="h6" color="error" sx={{ mb: 1 }}>Errors:</Typography>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        {csvPreview.errors.slice(0, 5).join('; ')}
                        {csvPreview.hasMore.errors && ' ...and more'}
                      </Alert>
                    </>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancelImport}>Cancel</Button>
              <Button 
                onClick={handleConfirmImport} 
                variant="contained"
                disabled={!csvPreview?.summary.validRows || csvPreview.summary.validRows === 0}
              >
                Import {csvPreview?.summary.validRows || 0} Q&A pairs
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default QnaData;
